import { SUPPORT_ID } from '@c/modals/HelpRequestModal';
import { Popover } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import Avatar from '@ui/Avatar';
import Button from '@ui/Button';
import SafeImage from '@ui/SafeImage';
import { deleteMessage } from '@util/firestore/messages';
import { getUserById } from '@util/firestore/users';
import { formatTimestamp } from '@util/index';
import { renderSimpleMarkdown } from '@util/text/simpleMarkdown';
import { ChatDocument, ChatMessageDocument } from '@util/types/firestore/chat';
import { UserDocument } from '@util/types/firestore/users';
import { useAuth } from 'context/AuthContext';
import Link from 'next/link';
import { useState } from 'react';

// Safety banner prefix - messages starting with this get grey banner styling
const SAFETY_BANNER_PREFIX = '[SAFETY_BANNER]';

const IMG_SIZE = 256;
const GEARFOCUS_ICON_URL = 'https://gearfocus.b-cdn.net/icons/gearfocus_icon.png';

const MessageImage = ({
  img,
  fallbackImg,
  onClick,
}: {
  img: string;
  fallbackImg?: string;
  onClick: (src: string) => void;
}) => {
  return (
    <SafeImage
      src={img}
      key={img}
      height={IMG_SIZE}
      width={IMG_SIZE}
      alt={'img'}
      fallbackSrc={fallbackImg}
      className="cursor-pointer rounded-2xl"
      onClick={() => onClick(img)}
    />
  );
};

interface MessageBubbleProps {
  chat: ChatDocument;
  chatMessage: ChatMessageDocument;
  fallbackImg?: string;
  onImageClick: (image: string) => void;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
  showTimestamp?: boolean;
}

interface MessageProps {
  message: ChatMessageDocument;
  timestamp: number;
  isSupport?: boolean;
  user?: UserDocument;
  fallbackImg?: string;
  onImageClick: (image: string) => void;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
  showTimestamp?: boolean;
}

// Outbound messages (messages YOU send) - right aligned, orange bubbles
// Structure: [empty space] [bubble max-w-[320px]] [avatar 32px]
const MessageOutbound = ({
  message,
  timestamp,
  fallbackImg,
  onImageClick,
  user,
  isSupport,
  isLastInGroup = true,
  showTimestamp = true,
}: MessageProps) => (
  <div className="flex flex-row-reverse items-end gap-[0.8rem]">
    {/* Avatar column - fixed 32px width, only show content on last in group */}
    <div className="w-[3.2rem] h-[3.2rem] shrink-0">
      {isLastInGroup && (
        user?.roles?.admin ? (
          <div className="flex h-[3.2rem] w-[3.2rem] items-center justify-center overflow-hidden rounded-full bg-white shadow-xs">
            <SafeImage
              src={GEARFOCUS_ICON_URL}
              alt="GearFocus Support"
              width={32}
              height={32}
            />
          </div>
        ) : (
          <Avatar size="extraSmall" user={user} />
        )
      )}
    </div>

    {/* Message bubble - constrained width */}
    <div className="flex flex-col items-end gap-[0.2rem]">
      {!message.is_img ? (
        <div
          className={`max-w-[320px] px-[1.4rem] py-[0.8rem] text-[1.4rem] leading-[1.8rem] text-white bg-brand-primary
            ${isLastInGroup ? 'rounded-bubble-outbound' : 'rounded-bubble-outbound-grouped'}`}
        >
          <div className="break-words">{renderSimpleMarkdown(message.content)}</div>
        </div>
      ) : (
        <MessageImage
          img={message.thumbnail ?? message.content}
          fallbackImg={fallbackImg}
          onClick={onImageClick}
        />
      )}
      {showTimestamp && !!timestamp && (
        <p className="text-[1.1rem] text-brand-gray">
          {formatTimestamp(timestamp)}
        </p>
      )}
    </div>
  </div>
);

// Inbound messages (messages THEY send) - left aligned, gray bubbles
// Structure: [avatar 32px] [bubble max-w-[320px]] [empty space]
const MessageInbound = ({
  message,
  timestamp,
  isSupport,
  user,
  onImageClick,
  isFirstInGroup = true,
  isLastInGroup = true,
  showTimestamp = true,
}: MessageProps) => (
  <div className="flex items-end gap-[0.8rem]">
    {/* Avatar column - fixed 32px width, only show content on last in group */}
    <div className="w-[3.2rem] h-[3.2rem] shrink-0">
      {isLastInGroup && (
        message.uid === 'GearBot' ? (
          <div className="flex h-[3.2rem] w-[3.2rem] items-center justify-center overflow-hidden rounded-full bg-white shadow-xs">
            <SafeImage
              src={GEARFOCUS_ICON_URL}
              alt="GearBot"
              width={32}
              height={32}
            />
          </div>
        ) : isSupport ? (
          <Avatar size="extraSmall" user={user} />
        ) : (
          <Link href={`/shop-seller/${user?.username_slug}`}>
            <Avatar size="extraSmall" user={user} />
          </Link>
        )
      )}
    </div>

    {/* Message bubble - constrained width */}
    <div className="flex flex-col items-start gap-[0.2rem]">
      {!message.is_img ? (
        <div
          className={`max-w-[320px] px-[1.4rem] py-[0.8rem] text-[1.4rem] leading-[1.8rem]
            ${isSupport
              ? 'bg-brand-secondary text-white'
              : 'bg-brand-lightest-gray text-brand-black'}
            ${isLastInGroup ? 'rounded-bubble-inbound' : 'rounded-bubble-inbound-grouped'}`}
        >
          <div className="break-words">{renderSimpleMarkdown(message.content)}</div>
        </div>
      ) : (
        <MessageImage
          img={message.thumbnail ?? message.content}
          onClick={onImageClick}
        />
      )}
      {showTimestamp && !!timestamp && (
        <p className="text-[1.1rem] text-brand-gray">
          {formatTimestamp(timestamp)}
        </p>
      )}
    </div>
  </div>
);

// System message - centered bubble for offer accepted/declined, shipping updates, etc.
const MessageSystem = ({
  message,
  timestamp,
  showTimestamp = true,
}: {
  message: ChatMessageDocument;
  timestamp: number;
  showTimestamp?: boolean;
}) => {
  // Negative-outcome system messages (declined / expired) render red; everything
  // else (offer received, counter, accepted) renders green. Content here is a
  // controlled system string set by the backend, never user-typed text. GB #1317.
  const isNegative = /declined|expired/i.test(message.content);
  const bgColor = isNegative ? 'bg-red-100' : 'bg-green-100';

  return (
    <div className="flex flex-col items-center gap-[0.4rem] my-[0.8rem]">
      <div className={`rounded-[1.8rem] ${bgColor} px-[1.6rem] py-[0.8rem] text-center max-w-[320px]`}>
        <div className="text-[1.3rem] text-brand-black">{renderSimpleMarkdown(message.content)}</div>
      </div>
      {showTimestamp && !!timestamp && (
        <p className="text-[1.1rem] text-brand-gray">
          {formatTimestamp(timestamp)}
        </p>
      )}
    </div>
  );
};

// Safety banner - centered bubble style for safety warnings (contact info, payment apps)
// Uses same style as offer accepted/declined notifications but with grey background
const SafetyBanner = ({
  message,
  timestamp,
  showTimestamp = true,
}: {
  message: ChatMessageDocument;
  timestamp: number;
  showTimestamp?: boolean;
}) => {
  // Remove the [SAFETY_BANNER] prefix before displaying
  const displayContent = message.content.replace(SAFETY_BANNER_PREFIX, '');

  return (
    <div className="flex flex-col items-center gap-[0.4rem] my-[0.8rem]">
      <div className="max-w-[320px] rounded-[1.8rem] bg-gray-200 px-[1.6rem] py-[0.8rem] text-center">
        <div className="text-[1.3rem] text-brand-black">{renderSimpleMarkdown(displayContent)}</div>
      </div>
      {showTimestamp && !!timestamp && (
        <p className="text-[1.1rem] text-brand-gray">
          {formatTimestamp(timestamp)}
        </p>
      )}
    </div>
  );
};

// Check if a message is a safety banner
const isSafetyBanner = (content: string): boolean => {
  return content.startsWith(SAFETY_BANNER_PREFIX);
};

export default function MessageBubble({
  chat,
  chatMessage,
  fallbackImg,
  onImageClick,
  isFirstInGroup = true,
  isLastInGroup = true,
  showTimestamp = true,
}: MessageBubbleProps) {
  const { userDoc } = useAuth();

  const { data: messageUser } = useQuery(
    ['user', chatMessage.uid],
    () => getUserById(chatMessage.uid),
    {
      enabled: !!chatMessage.uid,
    }
  );
  const [menuOpen, setMenuOpen] = useState(false);

  if (!userDoc) return null;

  // Check if this is a safety banner (contact info/payment app warning)
  const isSafety = !chatMessage.is_img && isSafetyBanner(chatMessage.content);

  if (isSafety) {
    return (
      <SafetyBanner
        message={chatMessage}
        timestamp={chatMessage.created_at ?? 0}
        showTimestamp={showTimestamp}
      />
    );
  }

  // System messages (offer received/accepted/declined, counters, shipping) are
  // flagged explicitly by the backend via is_system — never inferred from message
  // text, so a user typing "Great news!" can't be mis-styled as a banner. GB #1317.
  const isSystem = !chatMessage.is_img && chatMessage.is_system === true;

  if (isSystem) {
    return (
      <MessageSystem
        message={chatMessage}
        timestamp={chatMessage.created_at ?? 0}
        showTimestamp={showTimestamp}
      />
    );
  }

  const onRightClick = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (userDoc?.roles?.support) {
      ev.preventDefault();
      ev.stopPropagation();
      setMenuOpen(true);
    }
  };

  const isOutbound = chatMessage.uid === userDoc?.uid;

  return (
    <div onContextMenu={onRightClick}>
      {isOutbound ? (
        <MessageOutbound
          message={chatMessage}
          timestamp={chatMessage.created_at ?? 0}
          fallbackImg={fallbackImg}
          user={messageUser}
          isSupport={userDoc.roles?.admin}
          onImageClick={onImageClick}
          isFirstInGroup={isFirstInGroup}
          isLastInGroup={isLastInGroup}
          showTimestamp={showTimestamp}
        />
      ) : (
        <MessageInbound
          message={chatMessage}
          timestamp={chatMessage.created_at ?? 0}
          isSupport={chatMessage.uid === SUPPORT_ID}
          user={messageUser}
          fallbackImg={fallbackImg}
          onImageClick={onImageClick}
          isFirstInGroup={isFirstInGroup}
          isLastInGroup={isLastInGroup}
          showTimestamp={showTimestamp}
        />
      )}
      {menuOpen && (
        <Popover className={`mt-1 ${isOutbound ? 'text-right' : ''}`}>
          <Popover.Panel static>
            <Button
              width="fluid"
              align={isOutbound ? 'right' : 'left'}
              type="text"
              text="Delete message"
              noXPadding
              onClick={() => deleteMessage(chat.id!, chatMessage)}
            />
            <Button
              width="fluid"
              align={isOutbound ? 'right' : 'left'}
              type="text"
              text="Cancel"
              noXPadding
              onClick={() => setMenuOpen(false)}
            />
          </Popover.Panel>
        </Popover>
      )}
    </div>
  );
}
